import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './SafeIcon';

const { FiMenu, FiX, FiEdit3, FiChevronDown, FiArrowRight } = FiIcons;

/* Studio origin for cross-subdomain auth-status probe + workspace links.
 * Dev default → studio-dev; production build injects VITE_STUDIO_ORIGIN. */
const STUDIO_ORIGIN = import.meta.env.VITE_STUDIO_ORIGIN
  || 'https://studio-dev.manuscriptsmith.com';

/* Grouped top-nav model, shared by the homepage nav and every content page.
 * Two grouped items render as dropdowns (desktop) / accordions (mobile). */
export const STUDIO_ITEMS = [
  { to: '/writing-studio', label: 'Writing Studio', desc: 'Draft & edit in your own voice' },
  { to: '/cover-studio', label: 'Cover Studio', desc: 'AI covers, KDP-ready' },
  { to: '/cover-showcase', label: 'Cover Gallery', desc: 'Browse AI cover examples' },
  { to: '/book-previewer', label: 'Book Previewer', desc: 'See your book before you publish' },
];
export const AUTHOR_INTEL_ITEMS = [
  { to: '/author-ai', label: 'Your Author AI', desc: 'An AI that knows your life — and guards it' },
  { to: '/voice-dna', label: 'Voice DNA', desc: 'Write in your own voice' },
  { to: '/author-persona', label: 'Author Persona', desc: 'A learning voice across every book' },
  { to: '/analysis', label: 'Analysis', desc: 'See your book, truly understood' },
];
export const AUDIOBOOK_ITEMS = [
  { to: '/audiobook', label: 'Overview', desc: 'Ebook → ACX-ready audiobook' },
  { to: '/audiobook/acx', label: 'ACX / Audible-Ready', desc: 'Mastered to the exact spec' },
  { to: '/audiobook/voices', label: 'Narration Voices', desc: 'Natural AI narrators matched to your book' },
];
/* Flat links, in the requested order (grouped items are interleaved in render). */
export const NAV_LINKS = [
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/compare', label: 'Compare' },
  { to: '/pricing', label: 'Pricing' },
];

const isActive = (pathname, group) => group.some((it) => pathname === it.to);

/* Desktop hover/click dropdown */
const Dropdown = ({ label, items, pathname }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const openNow = () => { clearTimeout(timer.current); setOpen(true); };
  const closeSoon = () => { timer.current = setTimeout(() => setOpen(false), 120); };
  const active = isActive(pathname, items);
  return (
    <div className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 whitespace-nowrap transition-colors ${open || active ? 'text-primary-700 font-semibold' : 'text-gray-600 hover:text-primary-600'}`}
        aria-haspopup="true" aria-expanded={open}
      >
        {label} <SafeIcon icon={FiChevronDown} className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-72 bg-white border border-parchment-200 rounded-2xl shadow-2xl p-2 z-50 whitespace-normal">
          {items.map((it) => (
            <Link
              key={it.to} to={it.to} onClick={() => setOpen(false)}
              className="block px-4 py-3 rounded-xl hover:bg-parchment-50 transition-colors group"
            >
              <div className={`text-sm font-semibold group-hover:text-primary-600 ${pathname === it.to ? 'text-primary-700' : 'text-gray-900'}`}>{it.label}</div>
              <div className="text-xs text-gray-500">{it.desc}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const SiteHeader = () => {
  const [open, setOpen] = useState(false); // mobile menu
  const [authStatus, setAuthStatus] = useState({ loggedIn: false, role: null });
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);

  // Mount-time, non-blocking cross-subdomain auth probe. Graceful on any failure:
  // network error / non-2xx / malformed JSON / 3s timeout → stay unauthenticated.
  useEffect(() => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 3000);
    fetch(`${STUDIO_ORIGIN}/studio/api/auth/status`, {
      credentials: 'include',
      signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data && typeof data.loggedIn === 'boolean') setAuthStatus(data); })
      .catch(() => { /* silently fall back — unauthenticated state */ })
      .finally(() => clearTimeout(timer));
    return () => ctrl.abort();
  }, []);

  const workspaceUrl = authStatus.loggedIn
    ? (authStatus.role === 'admin' || authStatus.role === 'support'
        ? `${STUDIO_ORIGIN}/studio/app/admin`
        : `${STUDIO_ORIGIN}/studio/app`)
    : null;

  const linkCls = (to) =>
    `whitespace-nowrap transition-colors ${pathname === to ? 'text-primary-700 font-semibold' : 'text-gray-600 hover:text-primary-600'}`;

  return (
    <header className="border-b border-parchment-200 bg-white/90 backdrop-blur-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0" aria-label="ManuscriptSmith home">
          <div className="relative flex items-center justify-center w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg shadow-lg overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 bg-gray-900 rounded-md flex items-center justify-center relative">
                <div className="absolute inset-0">
                  <div className="absolute top-1 left-1 w-2 h-0.5 bg-green-400"></div>
                  <div className="absolute top-1 left-1 w-0.5 h-2 bg-green-400"></div>
                  <div className="absolute top-1 right-1 w-2 h-0.5 bg-blue-400"></div>
                  <div className="absolute top-1 right-1 w-0.5 h-2 bg-blue-400"></div>
                  <div className="absolute bottom-1 left-1 w-2 h-0.5 bg-yellow-400"></div>
                  <div className="absolute bottom-1 left-1 w-0.5 h-2 bg-yellow-400"></div>
                  <div className="absolute bottom-1 right-1 w-2 h-0.5 bg-purple-400"></div>
                  <div className="absolute bottom-1 right-1 w-0.5 h-2 bg-purple-400"></div>
                </div>
                <div className="w-5 h-5 bg-white rounded-sm flex items-center justify-center">
                  <span className="text-[10px] font-extrabold text-primary-600">MS</span>
                </div>
              </div>
            </div>
          </div>
          <span className="text-xl font-bold hidden sm:inline">
            <span className="text-primary-600">Manuscript</span>
            <span className="text-secondary-600">Smith</span>
          </span>
        </Link>

        {/* desktop nav */}
        <nav className="hidden lg:flex flex-nowrap items-center gap-3.5 text-[14px] flex-1 justify-center whitespace-nowrap" aria-label="Primary">
          <Link to="/how-it-works" className={linkCls('/how-it-works')}>How It Works</Link>
          <Dropdown label="Studio" items={STUDIO_ITEMS} pathname={pathname} />
          <Dropdown label="Author Intelligence" items={AUTHOR_INTEL_ITEMS} pathname={pathname} />
          <Dropdown label="Audiobook" items={AUDIOBOOK_ITEMS} pathname={pathname} />
          <Link to="/compare" className={linkCls('/compare')}>Compare</Link>
          <Link to="/pricing" className={linkCls('/pricing')}>Pricing</Link>
        </nav>

        <div className="flex items-center gap-2 flex-shrink-0">
          {!authStatus.loggedIn && (
            <a href="https://app.manuscriptsmith.com/login" aria-label="Log in" className="hidden sm:inline text-sm text-gray-600 hover:text-primary-600 px-2">Log in</a>
          )}
          <a
            href={authStatus.loggedIn ? workspaceUrl : 'https://app.manuscriptsmith.com/signup'}
            aria-label={authStatus.loggedIn ? 'Go to your workspace' : 'Log in'}
            className="relative inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-sm font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-600"
          >
            {authStatus.loggedIn && (
              <span className="absolute -top-1 -right-1 bg-green-500 w-2 h-2 rounded-full" aria-hidden="true"></span>
            )}
            <SafeIcon icon={authStatus.loggedIn ? FiArrowRight : FiEdit3} className="w-4 h-4 mr-1.5" />
            {authStatus.loggedIn ? 'Go to Workspace' : 'Get Started'}
          </a>
          <button className="lg:hidden p-2 text-gray-700" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu" aria-expanded={open}>
            <SafeIcon icon={open ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* mobile menu */}
      {open && (
        <nav className="lg:hidden border-t border-parchment-200 bg-white px-4 py-3 space-y-1 max-h-[80vh] overflow-y-auto" aria-label="Mobile">
          <Link to="/how-it-works" onClick={() => setOpen(false)} className={`block px-3 py-2 rounded-lg ${pathname === '/how-it-works' ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>How It Works</Link>

          <div className="px-3 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400">Studio</div>
          {STUDIO_ITEMS.map((it) => (
            <Link key={it.to} to={it.to} onClick={() => setOpen(false)} className={`block px-3 py-2 rounded-lg ${pathname === it.to ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>{it.label}</Link>
          ))}

          <div className="px-3 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400">Author Intelligence</div>
          {AUTHOR_INTEL_ITEMS.map((it) => (
            <Link key={it.to} to={it.to} onClick={() => setOpen(false)} className={`block px-3 py-2 rounded-lg ${pathname === it.to ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>{it.label}</Link>
          ))}

          <div className="px-3 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400">Audiobook</div>
          {AUDIOBOOK_ITEMS.map((it) => (
            <Link key={it.to} to={it.to} onClick={() => setOpen(false)} className={`block px-3 py-2 rounded-lg ${pathname === it.to ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>{it.label}</Link>
          ))}

          <div className="border-t border-parchment-200 my-3"></div>
          {NAV_LINKS.filter((n) => n.to !== '/how-it-works').map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className={`block px-3 py-2 rounded-lg ${pathname === n.to ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>{n.label}</Link>
          ))}
          <a href={authStatus.loggedIn ? workspaceUrl : 'https://app.manuscriptsmith.com/login'} onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-50">{authStatus.loggedIn ? 'Go to Workspace' : 'Log in'}</a>
        </nav>
      )}
    </header>
  );
};

export default SiteHeader;
